import { useQuery } from '@tanstack/react-query'
import { Lightbulb, Sparkles, RefreshCw } from 'lucide-react'
import { supabase } from '../../lib/supabase'

interface Props {
  householdId:   string
  householdSize: number
  isPremium:     boolean
}

interface Insight {
  insight: string
  item:    string
  saving?: string
  tip?:    string
}

export default function WasteInsight({ householdId, householdSize, isPremium }: Props) {
  const { data: insight, isLoading, refetch } = useQuery<Insight | null>({
    queryKey: ['waste-insight', householdId],
    enabled:  !!householdId && isPremium,
    staleTime: 1000 * 60 * 60 * 24,
    queryFn: async () => {
      const { data: wasteEvents } = await supabase
        .from('expiration_events')
        .select(`
          item_id, wasted_dollars, price_paid,
          expiration_date, confirmed_at,
          pantry_items(name, display_category)
        `)
        .eq('household_id', householdId)
        .eq('used_before_expiry', false)
        .not('confirmed_at', 'is', null)
        .order('confirmed_at', { ascending: false })
        .limit(20)

      if (!wasteEvents || wasteEvents.length < 3) return null

      const wasteData = wasteEvents.map(e => ({
        item: Array.isArray(e.pantry_items)
          ? e.pantry_items[0]?.name
          : (e.pantry_items as any)?.name,
        category: Array.isArray(e.pantry_items)
          ? e.pantry_items[0]?.display_category
          : (e.pantry_items as any)?.display_category,
        wastedDollars: e.wasted_dollars || e.price_paid || 0,
        date: e.confirmed_at,
      }))

      const response = await fetch('/api/claude-waste', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wasteData, householdSize }),
      })

      return response.json()
    },
  })

  // ── Premium upsell ────────────────────────────────────────────────────────

  if (!isPremium) {
    return (
      <div className="bg-cream-warm border border-sand/40 rounded-2xl px-5 py-4 mb-3">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-4 w-4 text-terracotta" strokeWidth={1.7} />
          <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-terracotta">
            Pantry+
          </p>
        </div>
        <p className="font-serif text-[14px] font-medium text-ink leading-tight">
          AI <em className="italic">waste</em> analysis
        </p>
        <p className="font-serif italic text-[12px] text-ink-muted mt-1.5 leading-[1.5]">
          Monthly insights about your waste patterns and how to reduce them.
        </p>
      </div>
    )
  }

  // ── Loading ───────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="bg-paper border border-border-sand rounded-2xl px-5 py-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="h-3.5 w-3.5 rounded-full border-2 border-forest border-t-transparent animate-spin shrink-0" />
          <p className="font-serif italic text-[13px] text-ink-muted">
            Analyzing your waste patterns…
          </p>
        </div>
      </div>
    )
  }

  // ── No data yet ───────────────────────────────────────────────────────────

  if (!insight) {
    return (
      <div className="bg-paper border border-border-sand rounded-2xl px-5 py-4 mb-3">
        <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-1">
          AI waste analysis
        </p>
        <p className="font-serif italic text-[13px] text-ink-muted leading-[1.5]">
          Track at least 3 expired items to get your first insight.
        </p>
      </div>
    )
  }

  // ── Insight ───────────────────────────────────────────────────────────────

  return (
    <div className="bg-paper border border-border-sand rounded-2xl px-5 py-4 mb-3">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted">
            Your waste this month
          </p>
          <h3 className="font-serif text-[15px] text-ink mt-0.5 leading-[1.2]">
            <em className="italic">AI</em> insight
          </h3>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          className="h-8 w-8 flex items-center justify-center rounded-full text-ink-muted hover:bg-cream-warm/40 transition-colors"
          aria-label="Refresh insight"
        >
          <RefreshCw className="h-3.5 w-3.5" strokeWidth={2} />
        </button>
      </div>

      <div className="bg-ok-bg/60 rounded-2xl px-4 py-3 mb-3">
        <p className="font-serif text-[13px] text-ink leading-[1.55]">
          {insight.insight}
        </p>
      </div>

      <div className="flex gap-3 mb-3">
        <div className="flex-1 bg-cream-warm/40 rounded-xl px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-[0.1em] font-medium text-ink-muted">Item</p>
          <p className="font-serif text-[13px] font-medium text-ink mt-0.5 truncate">
            {insight.item}
          </p>
        </div>
        {insight.saving && (
          <div className="flex-1 bg-cream-warm/40 rounded-xl px-3 py-2.5">
            <p className="text-[10px] uppercase tracking-[0.1em] font-medium text-ink-muted">
              Potential saving
            </p>
            <p className="font-serif text-[13px] font-medium text-forest mt-0.5">
              {insight.saving}/month
            </p>
          </div>
        )}
      </div>

      {insight.tip && (
        <div className="rounded-xl bg-cream-warm/40 px-3 py-2.5 flex items-start gap-2.5">
          <Lightbulb className="h-[14px] w-[14px] text-terracotta flex-shrink-0 mt-0.5" strokeWidth={1.7} />
          <p className="font-serif italic text-[12px] text-ink leading-[1.5]">
            {insight.tip}
          </p>
        </div>
      )}
    </div>
  )
}
